"use client";

import React from "react";

import { format } from "date-fns";
import Entity from "./Entity";
import {
  getCanonicalUrl,
  appendHttpsToUrl,
  formatImageUrlByDevice,
  _capitalizeFirstLetter,
  renderNextLink,
  renderPreviousLink,
  formatDescription,
} from "../helpers/Helper";
import Device from "../constants/Device"; /*
	This component will be used to render <head> for every page.
	Props:
		@url : url of page
		@pageType : Type of page ex Book, Contributor
		@props : Contains other property like title, desc, social media info
*/
import { useAppContext } from "./Provider";

/*
  renderHeadMeta function is used the render the meta and link tags inside the <head> tag
  @parameters:
  data : array of meta or link tags (seperately)
  type : It can be meta or link , To identify which tag need to render link or meta
*/
const renderHeadMeta = (data, type) => {
  //Get Category as NavItems
  if (type == "meta") {
    return (
      data &&
      data.map((meta, index) => {
        return meta.property ? (
          <meta
            key={meta.property || `meta-${index}`}
            property={meta.property}
            content={meta.content}
          />
        ) : (
          <meta
            key={meta.name || `meta-${index}`}
            name={meta.name}
            content={meta.content}
          />
        );
      })
    );
  } else if (type == "link") {
    return (
      data &&
      data.map((meta, index) => {
        return meta.rel ? (
          <link
            key={`${meta.rel}-${meta.href || index}`}
            rel={meta.rel}
            href={meta.href}
          />
        ) : (
          ""
        );
      })
    );
  }
};

const Meta = (props) => {
  let url = `${appendHttpsToUrl(props.url)}`,
    socialImage,
    title,
    description,
    keywords,
    ogType,
    ogTitle,
    ogDescription,
    ogImageWidth,
    twitterTitle,
    twitterDescription,
    metaOptions = [],
    linkOptions = [],
    SEOTitle,
    twitterCard = "summary_large_image",
    bookPublisher,
    bookISBN,
    bookReleaseDate,
    // siteName = props.siteName,
    socialTitle,
    sailTitle,
    category,
    noIndex,
    published_on,
    sailthruDescription,
    siteInterest;

  //If canonicalUrl isn't provided in ORION, build it. Based on https://openroadmedia.atlassian.net/browse/ORMTECH-864
  let canonicalURL = "";
  let { data, host, router } = props?.data || useAppContext(),
    { SiteSettings } = data || {},
    siteName = SiteSettings?.site_name;
  if (
    props.post &&
    props?.post?.seo &&
    props?.post?.seo?.canonical &&
    props?.post?.seo?.canonical.length > 0
  )
    canonicalURL = props?.post?.seo?.canonical;
  else
    canonicalURL =
      url !== "undefined"
        ? getCanonicalUrl(url)
        : getCanonicalUrl(`${host}${router?.asPath}`);

  switch (props.pageType) {
    case Entity.BOOK:
      {
        title = `${props.Book.title} by ${props.Book.authors[0].display_name} | ${siteName}`;
        description = formatDescription(title, props.Book.description, 200);
        twitterTitle = props.Book.title;
        twitterDescription = formatDescription(
          title,
          props.Book.description,
          140
        );
        ogType = "Book";
        ogTitle = props.Book.title;
        ogDescription = description;
        ogImageWidth = "400";
        socialImage =
          props.Book.image && props.Book.image
            ? `${appendHttpsToUrl(props.Book.image)}`
            : "";
        bookPublisher = props.Book.publisher;
        bookISBN = props.Book.primary_isbn;
        bookReleaseDate = props.Book.pub_date;
        noIndex = props.noIndex ? "noindex" : "index";
        keywords = props.Book.keywords && props.Book.keywords.join(",");
        metaOptions = [
          { property: "book:publisher", content: bookPublisher },
          { property: "book:isbn", content: bookISBN },
          { property: "book:release_date", content: bookReleaseDate },
          { property: "keywords", content: keywords },
        ];
      }
      break;
    case Entity.CONTRIBUTOR:
      {
        title = props.displayName;
        description = formatDescription(
          `Books written by ${title}`,
          props.shortBio,
          200
        );
        twitterTitle = title;
        twitterDescription = formatDescription(
          `Books written by ${title}`,
          props.shortBio,
          140
        );
        ogType = "Contributor";
        ogTitle = title;
        ogDescription = description;
        ogImageWidth = "300";
        socialImage =
          props.Image && props.Image ? `${appendHttpsToUrl(props.Image)}` : "";
        noIndex = props.noIndex ? "noindex" : "index";

        metaOptions = [{ property: "book:author", content: title }];
      }
      break;

    case Entity.STATIC:
      {
        title = props.static.Title;
        // description = props.static.Description;
        description = formatDescription(title, props.static.Description, 200);
        SEOTitle = props.static.SEOTitle
          ? props.static.SEOTitle
          : title;

        twitterTitle = SEOTitle ? SEOTitle : title;
        twitterDescription = formatDescription(
          title,
          props.static.Description,
          140
        );
        ogType = "Website";
        ogTitle = SEOTitle ? SEOTitle : title;
        ogDescription = description;
        socialImage =
          props.Homepage_SEO_Image && props.Homepage_SEO_Image
            ? `${appendHttpsToUrl(props.Homepage_SEO_Image)}`
            : "";
        noIndex = props.noIndex || props.static.noIndex ? "noindex" : "index";
        ogImageWidth = "400";

        linkOptions = [
          { ...renderNextLink(props.nextLink, props.isLastPage, props.domain) },
          {
            ...renderPreviousLink(
              props.previousLink,
              props.isFirstPage,
              props.domain
            ),
          },
        ];
      }
      break;
    case Entity.CATEGORY:
      {
        title = props.static.Title;
        description = formatDescription(title, props.static.Description, 200);
        sailthruDescription = formatDescription(
          title,
          props.static.SailthruDescription,
          200
        );
        SEOTitle = props.static.SEOTitle
          ? props.static.SEOTitle
          : title;
        twitterTitle = SEOTitle ? SEOTitle : title;
        twitterDescription = formatDescription(
          title,
          props.static.Description,
          140
        );
        ogType = "Website";
        ogTitle = SEOTitle ? SEOTitle : title;
        ogDescription = description;
        socialImage =
          props.Homepage_SEO_Image && props.Homepage_SEO_Image
            ? `${appendHttpsToUrl(props.Homepage_SEO_Image)}`
            : "";
        noIndex = props.noIndex || props.static.noIndex ? "noindex" : "index";
        ogImageWidth = "400";

        linkOptions = [
          { ...renderNextLink(props.nextLink, props.isLastPage, props.domain) },
          {
            ...renderPreviousLink(
              props.previousLink,
              props.isFirstPage,
              props.domain
            ),
          },
        ];

        metaOptions = [
          { name: "sailthru.description", content: sailthruDescription },
        ];
      }
      break;
    case Entity.SEARCHPAGE:
      {
        title = props.static.Title;
        description = props.static.Description;
        noIndex = props.static.noIndex ? "noindex" : "index";
      }
      break;
    case Entity.ERROR:
      {
        title = props.title;
        noIndex = "noindex";
      }
      break;
    //RT: For all posts and homepages .
    default:
      {
        title = props.post?.seo?.title || props.title;
        socialTitle = props.post?.seo?.socialtitle;
        description = props.post?.seo?.description;
        description = formatDescription(title, description, 200);
        twitterDescription = formatDescription(title, description, 140);
        socialImage =
          props?.post?.featured_media &&
          formatImageUrlByDevice(
            props.post.featured_media.name,
            Device.DESKTOP
          );
        twitterTitle = socialTitle ? socialTitle : title;
        ogType = "Article";
        ogTitle = socialTitle ? socialTitle : title;
        sailTitle = socialTitle ? socialTitle : title;
        ogDescription = description;
        ogImageWidth = "740";
        category =
          props?.post?.categories && props?.post?.categories.length
            ? props.post.categories[0].trim()
            : "";
        published_on = props.post?.published_on;
        noIndex =
          props.post?.seo?.no_index || props?.noIndex ? "noindex" : "index";
        sailthruDescription =
          props?.post?.seo?.sailthru_description || props?.post?.sub_title;
        siteInterest =
          props.post?.siteInterests?.map((el) => el._id.name).join(",") || "";
        metaOptions = [
          // Facebook
          { property: "og:update_time", content: published_on },
          { property: "article:section", content: category },
          { property: "article:published_time", content: published_on },
          {
            property: "article:modified_time",
            content: props.post?.last_modified,
          },
          //Sailthru
          { name: "sailthru.title", content: sailTitle },
          { name: "sailthru.image.thumb", content: socialImage },
          { name: "sailthru.image.full", content: socialImage },
          { name: "sailthru.description", content: sailthruDescription },
          { name: "sailthru.label", content: category },
          {
            name: "sailthru.tags",
            content:
              props.post?.tags &&
              props.post?.tags?.map((item) => item && item.name),
          },
          {
            name: "sailthru.date",
            content: props.post?.published_on
              ? format(new Date(props.post.published_on), "yyyy-MM-dd")
              : "",
          },
          {
            name: "sailthru.expire_date",
            content: props.post?.expires_on
              ? format(new Date(props.post.expires_on), "yyyy-MM-dd")
              : "",
          },
          { name: "sailthru.button", content: "" },
          {
            name: "sailthru.author",
            content:
              props.post?.byline && props.post?.byline.length > 0
                ? props.post?.byline[0]?.display_name
                : "",
          },
        ];
        if (siteInterest) {
          metaOptions.push({
            name: "sailthru.siteinterest",
            content: siteInterest,
          });
        }
      }
      break;
  }

  //Facebook
  metaOptions.push({ property: "og:title", content: ogTitle }),
    metaOptions.push({ property: "og:description", content: ogDescription }),
    metaOptions.push({ property: "og:url", content: url }),
    metaOptions.push({ property: "og:site_name", content: siteName }),
    metaOptions.push({ property: "og:image", content: socialImage }),
    metaOptions.push({ property: "og:image:width", content: ogImageWidth }),
    //Twitter
    metaOptions.push({ property: "twitter:card", content: twitterCard }),
    metaOptions.push({
      property: "twitter:description",
      content: twitterDescription,
    }),
    metaOptions.push({ property: "twitter:title", content: twitterTitle }),
    metaOptions.push({ property: "twitter:image", content: socialImage }),
    metaOptions.push({ name: "robots", content: noIndex }),
    linkOptions.push({ rel: "canonical", href: canonicalURL });

  const generateMetadataTags = (
    <>
      <title>{_capitalizeFirstLetter(title)}</title>
      <meta name="description" content={description} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content={ogType} />
      {renderHeadMeta(metaOptions, "meta")}
      {renderHeadMeta(linkOptions, "link")}
    </>
  );

  return (<>{generateMetadataTags}</>)
};

export default Meta;
